import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Loader2, ArrowLeft, FileText, BookOpen, Search, Shield, Users, Sparkles
} from 'lucide-react';

const CATEGORY_ICON = {
  Policies: Shield,
  Culture: Sparkles,
  Onboarding: Users,
  Compliance: Shield,
  Ops: BookOpen,
  General: FileText,
};

// Extremely small markdown-ish renderer — we only support headings, lists, bold, code fences.
function renderMarkdown(md) {
  if (!md) return null;
  const blocks = md.split(/\n{2,}/);
  return blocks.map((b, i) => {
    const trimmed = b.trim();
    if (trimmed.startsWith('## ')) {
      return <h2 key={i} className="text-lg font-semibold text-slate-800 mt-4">{trimmed.slice(3)}</h2>;
    }
    if (trimmed.startsWith('- ') || /^\d+\.\s/.test(trimmed)) {
      const isOrdered = /^\d+\.\s/.test(trimmed.split('\n')[0]);
      const items = trimmed.split('\n').map((l) => l.replace(/^-\s+|^\d+\.\s+/, ''));
      const ListTag = isOrdered ? 'ol' : 'ul';
      return (
        <ListTag key={i} className={`${isOrdered ? 'list-decimal' : 'list-disc'} ml-6 mt-2 space-y-1 text-slate-700`}>
          {items.map((it, j) => (
            <li key={j} dangerouslySetInnerHTML={{ __html: it.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/`(.*?)`/g, '<code class="bg-slate-100 px-1 rounded">$1</code>') }} />
          ))}
        </ListTag>
      );
    }
    return (
      <p key={i} className="text-slate-700 mt-2 leading-relaxed"
         dangerouslySetInnerHTML={{ __html: trimmed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/`(.*?)`/g, '<code class="bg-slate-100 px-1 rounded">$1</code>') }} />
    );
  });
}

const EmployeeCenter = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [doc, setDoc] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/hr/documents');
      setCategories(res.data.categories || []);
    } catch {
      toast.error('Failed to load Employee Center');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!slug) { setDoc(null); return; }
    (async () => {
      try {
        const res = await api.get(`/hr/documents/${slug}`);
        setDoc(res.data);
      } catch {
        toast.error('Document not found');
        navigate('/employee/center');
      }
    })();
  }, [slug, api, navigate]);

  const filteredCategories = categories.map((c) => ({
    ...c,
    docs: c.docs.filter((d) =>
      (d.title + ' ' + (d.summary || '')).toLowerCase().includes(search.toLowerCase())
    ),
  })).filter((c) => c.docs.length > 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => (slug ? navigate('/employee/center') : navigate(-1))} data-testid="ec-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> {slug ? 'All documents' : 'Back'}
            </Button>
            <h1 className="font-semibold text-slate-800 mt-1 flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-emerald-500" /> Employee Center
            </h1>
            <p className="text-xs text-slate-500">
              Remote work guidelines, culture norms, compliance, and everything else you need as part of the Zazu team.
            </p>
          </div>
          {!slug && (
            <div className="relative w-64 max-w-full">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
              <input
                className="pl-9 pr-3 py-2 rounded-md border border-slate-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-emerald-400"
                placeholder="Search documents..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="ec-search"
              />
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 md:p-6">
        {loading && !doc ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : slug && doc ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6 md:p-8">
              <Badge className="bg-emerald-100 text-emerald-700 mb-3">{doc.category}</Badge>
              <h1 className="text-2xl font-bold text-slate-800">{doc.title}</h1>
              {doc.summary && <p className="text-slate-500 mt-1">{doc.summary}</p>}
              <div className="mt-6 prose prose-slate max-w-none" data-testid="ec-doc-body">
                {renderMarkdown(doc.body_markdown)}
              </div>
              <p className="text-[11px] text-slate-400 mt-8 border-t pt-4">
                Last updated {doc.updated_at ? doc.updated_at.split('T')[0] : 'recently'} · {doc.updated_by || doc.created_by || 'seed'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Quick nav tiles */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Link to="/employee/resources" data-testid="ec-tile-resources">
                <Card className="border-2 border-emerald-200 hover:border-emerald-400 hover:shadow-md transition-all cursor-pointer h-full">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-800 text-sm">Resource Center</p>
                      <p className="text-xs text-slate-500">Templates, brand kits, policies — download anytime.</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/employee/equipment" data-testid="ec-tile-equipment">
                <Card className="border-2 border-indigo-200 hover:border-indigo-400 hover:shadow-md transition-all cursor-pointer h-full">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-800 text-sm">Request Equipment</p>
                      <p className="text-xs text-slate-500">Ask HR for a laptop, phone or other work equipment.</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/profile" data-testid="ec-tile-profile">
                <Card className="border-2 border-slate-200 hover:border-slate-400 hover:shadow-md transition-all cursor-pointer h-full">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-800 text-sm">My Profile & HR Info</p>
                      <p className="text-xs text-slate-500">Update your phone, address, emergency contact.</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </div>

            {filteredCategories.length === 0 && (
              <p className="text-center text-slate-400 italic py-10">No documents match &ldquo;{search}&rdquo;.</p>
            )}
            {filteredCategories.map((cat) => {
              const Icon = CATEGORY_ICON[cat.name] || FileText;
              return (
                <div key={cat.name}>
                  <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-1.5">
                    <Icon className="w-3.5 h-3.5" /> {cat.name}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {cat.docs.map((d) => (
                      <Link key={d.id} to={`/employee/center/${d.slug}`} data-testid={`ec-doc-${d.slug}`}>
                        <Card className="border-0 shadow-sm hover:shadow-md hover:scale-[1.01] transition-all cursor-pointer h-full">
                          <CardContent className="p-4">
                            <p className="font-semibold text-slate-800">{d.title}</p>
                            {d.summary && <p className="text-xs text-slate-500 mt-1 leading-relaxed line-clamp-2">{d.summary}</p>}
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default EmployeeCenter;
